import React, { useState, useCallback, useMemo } from 'react';
import { Modal } from '@components/Dialog';
import { Button } from '@components/Button';
import {
  SalesOrder,
  SplitOrderItemAllocation,
  OrderStatusId,
} from '../../types/salesOrder';
import { ArrowRight, ArrowLeft, Scissors, AlertTriangle, GripVertical, RefreshCw } from 'lucide-react';

// ─── Props ───────────────────────────────────────────────────────

interface SplitOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (
    order1StatusId: number,
    order2StatusId: number,
    order1Items: { sku: string; quantity: number; unit_price?: number; unit_cost?: number; discount?: number }[],
    order2Items: { sku: string; quantity: number; unit_price?: number; unit_cost?: number; discount?: number }[],
    replacementItemsTarget: 1 | 2 | null,
  ) => void;
  order: SalesOrder;
  loading?: boolean;
  error?: string | null;
  statusOptions?: { id: number; value: string; label: string; color: string }[];
}

// ─── Helpers ─────────────────────────────────────────────────────

const formatCurrency = (value: number): string =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const round2 = (value: number): number => Math.round(value * 100) / 100;

// ─── Component ───────────────────────────────────────────────────

export const SplitOrderModal: React.FC<SplitOrderModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  order,
  loading = false,
  error = null,
  statusOptions = [],
}) => {
  const items = order.items || [];
  const replacementItems = order.replacement_items || [];
  const hasReplacementItems = replacementItems.length > 0;
  const isResplit = Boolean(order.parent_order_id);

  // Labels adapt based on whether the order already has a parent
  const order1Label = isResplit ? `Pedido Atual #${order.order_id}` : 'Pedido 1';
  const order2Label = isResplit ? 'Novo Pedido (irmão)' : 'Pedido 2';

  const [allocations, setAllocations] = useState<SplitOrderItemAllocation[]>(() =>
    items.map(item => ({
      order_item_id: item.order_item_id,
      sku: item.sku,
      description: item.description,
      total_quantity: item.quantity,
      order1_quantity: item.quantity, // all items start in order 1
      order2_quantity: 0,
      unit_price: item.unit_price,
      unit_cost: item.unit_cost,
      discount: item.discount,
    })),
  );

  const [order1Status, setOrder1Status] = useState(OrderStatusId.EmAndamento);
  const [order2Status, setOrder2Status] = useState(OrderStatusId.EmAndamento);
  const [splitItemIdx, setSplitItemIdx] = useState<number | null>(null);
  const [splitQty, setSplitQty] = useState('');
  const [replacementTarget, setReplacementTarget] = useState<1 | 2>(1);

  // Calculate proportional distribution preview
  const order1Total = useMemo(() => {
    return allocations.reduce((sum, a) => {
      const price = (a.unit_price || 0) * (1 - (a.discount || 0) / 100);
      return sum + price * a.order1_quantity;
    }, 0);
  }, [allocations]);

  const order2Total = useMemo(() => {
    return allocations.reduce((sum, a) => {
      const price = (a.unit_price || 0) * (1 - (a.discount || 0) / 100);
      return sum + price * a.order2_quantity;
    }, 0);
  }, [allocations]);

  // O rateio abaixo espelha domain.ComputeSplitDistribution no ERPApi: proporcional ao valor
  // líquido de produtos de cada filho, com o Pedido 2 recebendo a sobra do arredondamento para que
  // a soma dos dois feche exatamente com o pedido de origem.
  const grandTotal = order1Total + order2Total;
  const order1Ratio = grandTotal > 0 ? order1Total / grandTotal : 0.5;
  const order2Ratio = 1 - order1Ratio;

  const shippingPrice = order.shipping_price || 0;
  const orderDiscount = order.order_discount || 0;
  const replacementDiscount = hasReplacementItems ? order.product_replacement_discount_value || 0 : 0;

  const order1Freight = round2(shippingPrice * order1Ratio);
  const order2Freight = round2(shippingPrice - order1Freight);
  const order1Discount = round2(orderDiscount * order1Ratio);
  const order2Discount = round2(orderDiscount - order1Discount);
  // O desconto de troca/devolução não é rateado: vai inteiro para o filho que recebe os itens de troca.
  const order1ReplacementDiscount = replacementTarget === 1 ? replacementDiscount : 0;
  const order2ReplacementDiscount = replacementTarget === 2 ? replacementDiscount : 0;

  // Total que ficará em cada pedido filho. Mesma fórmula do OrderSellTotal que o backend grava no
  // desmembramento: produtos + frete − desconto do pedido − desconto de troca/devolução.
  const order1SellTotal = round2(order1Total + order1Freight - order1Discount - order1ReplacementDiscount);
  const order2SellTotal = round2(order2Total + order2Freight - order2Discount - order2ReplacementDiscount);

  // Move all quantity of an item to order 2
  const moveToOrder2 = useCallback((idx: number) => {
    setAllocations(prev => prev.map((a, i) => i === idx
      ? { ...a, order1_quantity: 0, order2_quantity: a.total_quantity }
      : a
    ));
  }, []);

  // Move all quantity of an item to order 1
  const moveToOrder1 = useCallback((idx: number) => {
    setAllocations(prev => prev.map((a, i) => i === idx
      ? { ...a, order1_quantity: a.total_quantity, order2_quantity: 0 }
      : a
    ));
  }, []);

  // Split quantity for an item
  const handleSplitItem = useCallback((idx: number) => {
    setSplitItemIdx(idx);
    setSplitQty(String(Math.floor(allocations[idx].total_quantity / 2)));
  }, [allocations]);

  const confirmSplit = useCallback(() => {
    if (splitItemIdx === null) return;
    const qty = parseInt(splitQty, 10);
    const alloc = allocations[splitItemIdx];
    if (isNaN(qty) || qty < 0 || qty > alloc.total_quantity) return;

    setAllocations(prev => prev.map((a, i) => i === splitItemIdx
      ? { ...a, order1_quantity: qty, order2_quantity: a.total_quantity - qty }
      : a
    ));
    setSplitItemIdx(null);
    setSplitQty('');
  }, [splitItemIdx, splitQty, allocations]);

  // Validation
  const allItemsAllocated = allocations.every(
    a => a.order1_quantity + a.order2_quantity === a.total_quantity,
  );
  const order1HasItems = allocations.some(a => a.order1_quantity > 0);
  const order2HasItems = allocations.some(a => a.order2_quantity > 0);
  const canConfirm = allItemsAllocated && order1HasItems && order2HasItems;

  const handleConfirm = () => {
    const o1Items = allocations
      .filter(a => a.order1_quantity > 0)
      .map(a => ({ sku: a.sku, quantity: a.order1_quantity, unit_price: a.unit_price, unit_cost: a.unit_cost, discount: a.discount }));
    const o2Items = allocations
      .filter(a => a.order2_quantity > 0)
      .map(a => ({ sku: a.sku, quantity: a.order2_quantity, unit_price: a.unit_price, unit_cost: a.unit_cost, discount: a.discount }));
    onConfirm(order1Status, order2Status, o1Items, o2Items, hasReplacementItems ? replacementTarget : null);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" closeOnBackdrop={!loading}>
      <div className="px-6 py-4 border-b border-slate-200">
        <h2 className="text-lg font-bold text-slate-900">Desmembrar Pedido #{order.order_id}</h2>
        {isResplit ? (
          <p className="text-sm text-slate-500 mt-1">
            Este pedido é filho do pedido <span className="font-semibold">#{order.parent_order_id}</span>. 
            Os itens movidos para o novo pedido criarão um pedido irmão (mesmo pai #{order.parent_order_id}). 
            O pedido atual será mantido com os itens restantes.
          </p>
        ) : (
          <p className="text-sm text-slate-500 mt-1">
            Distribua os itens entre os novos pedidos. O frete, desconto e parcelas serão distribuídos proporcionalmente ao valor dos produtos em cada pedido filho.
          </p>
        )}
      </div>

      <div className="px-6 py-4 max-h-[70vh] overflow-y-auto custom-scrollbar">
        {/* Status selection */}
        <div className={`grid gap-4 mb-6 ${isResplit ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-2'}`}>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Status — {order1Label}</label>
            <select
              className="input w-full text-sm"
              value={order1Status}
              onChange={e => setOrder1Status(Number(e.target.value))}
            >
              {statusOptions.map(s => (
                <option key={s.id} value={s.id}>{s.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Status — {order2Label}</label>
            <select
              className="input w-full text-sm"
              value={order2Status}
              onChange={e => setOrder2Status(Number(e.target.value))}
            >
              {statusOptions.map(s => (
                <option key={s.id} value={s.id}>{s.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Items allocation */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Order 1 */}
          <div className="border border-blue-200 rounded-lg p-4 bg-blue-50/50">
            <div className="flex items-baseline justify-between gap-2 mb-3">
              <h3 className="font-semibold text-sm text-blue-800">{order1Label}</h3>
              <span className="text-sm font-bold text-blue-800" title="Total que ficará neste pedido">
                {formatCurrency(order1SellTotal)}
              </span>
            </div>
            <div className="space-y-2">
              {allocations.map((alloc, idx) => {
                if (alloc.order1_quantity === 0) return null;
                return (
                  <div key={alloc.sku + '-o1'} className="flex items-center justify-between bg-white rounded border border-blue-100 p-2 text-sm">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <GripVertical size={14} className="text-slate-400 flex-shrink-0" />
                      <div className="min-w-0">
                        <span className="font-medium text-slate-900">{alloc.sku}</span>
                        <span className="text-slate-500 ml-1 truncate block text-xs">{alloc.description}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                      <span className="text-xs font-bold bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                        x{alloc.order1_quantity}
                      </span>
                      {alloc.total_quantity > 1 && (
                        <button
                          onClick={() => handleSplitItem(idx)}
                          className="p-1 text-amber-600 hover:bg-amber-50 rounded"
                          title="Dividir quantidade"
                        >
                          <Scissors size={14} />
                        </button>
                      )}
                      <button
                        onClick={() => moveToOrder2(idx)}
                        className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                        title="Mover para Pedido 2"
                      >
                        <ArrowRight size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
              {!allocations.some(a => a.order1_quantity > 0) && (
                <div className="text-xs text-slate-400 text-center py-4">Nenhum item</div>
              )}
            </div>
          </div>

          {/* Order 2 */}
          <div className="border border-emerald-200 rounded-lg p-4 bg-emerald-50/50">
            <div className="flex items-baseline justify-between gap-2 mb-3">
              <h3 className="font-semibold text-sm text-emerald-800">{order2Label}</h3>
              <span className="text-sm font-bold text-emerald-800" title="Total que ficará neste pedido">
                {formatCurrency(order2SellTotal)}
              </span>
            </div>
            <div className="space-y-2">
              {allocations.map((alloc, idx) => {
                if (alloc.order2_quantity === 0) return null;
                return (
                  <div key={alloc.sku + '-o2'} className="flex items-center justify-between bg-white rounded border border-emerald-100 p-2 text-sm">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <GripVertical size={14} className="text-slate-400 flex-shrink-0" />
                      <div className="min-w-0">
                        <span className="font-medium text-slate-900">{alloc.sku}</span>
                        <span className="text-slate-500 ml-1 truncate block text-xs">{alloc.description}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                      <span className="text-xs font-bold bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded">
                        x{alloc.order2_quantity}
                      </span>
                      {alloc.total_quantity > 1 && (
                        <button
                          onClick={() => handleSplitItem(idx)}
                          className="p-1 text-amber-600 hover:bg-amber-50 rounded"
                          title="Dividir quantidade"
                        >
                          <Scissors size={14} />
                        </button>
                      )}
                      <button
                        onClick={() => moveToOrder1(idx)}
                        className="p-1 text-emerald-600 hover:bg-emerald-100 rounded"
                        title={`Mover para ${order1Label}`}
                      >
                        <ArrowLeft size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
              {!allocations.some(a => a.order2_quantity > 0) && (
                <div className="text-xs text-slate-400 text-center py-4">Nenhum item — mova itens do {order1Label} para cá</div>
              )}
            </div>
          </div>
        </div>

        {/* Split item dialog */}
        {splitItemIdx !== null && (
          <div className="mt-4 border border-amber-200 rounded-lg p-4 bg-amber-50">
            <div className="flex items-center gap-2 mb-2">
              <Scissors size={16} className="text-amber-600" />
              <span className="font-semibold text-sm text-amber-800">
                Dividir item: {allocations[splitItemIdx].sku} — {allocations[splitItemIdx].description}
              </span>
            </div>
            <p className="text-xs text-amber-700 mb-2">
              Total: {allocations[splitItemIdx].total_quantity} unidades. Defina quantas ficam no {order1Label}:
            </p>
            <div className="flex items-center gap-3">
              <div>
                <label className="text-xs text-slate-600">{order1Label}</label>
                <input
                  type="number"
                  className="input w-20 text-sm"
                  value={splitQty}
                  min={0}
                  max={allocations[splitItemIdx].total_quantity}
                  onChange={e => setSplitQty(e.target.value)}
                />
              </div>
              <div>
                <label className="text-xs text-slate-600">{order2Label}</label>
                <input
                  type="number"
                  className="input w-20 text-sm"
                  value={allocations[splitItemIdx].total_quantity - (parseInt(splitQty, 10) || 0)}
                  readOnly
                />
              </div>
              <div className="flex gap-2 mt-4">
                <Button size="sm" onClick={confirmSplit}>Aplicar</Button>
                <Button size="sm" variant="outline" onClick={() => setSplitItemIdx(null)}>Cancelar</Button>
              </div>
            </div>
          </div>
        )}

        {/* Totais por pedido filho */}
        <div className="mt-4 border border-slate-200 rounded-lg p-4 bg-slate-50">
          <h4 className="text-xs font-semibold text-slate-700 uppercase mb-3">Valores por Pedido (Prévia)</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            {([
              {
                label: order1Label,
                accent: 'text-blue-700',
                ratio: order1Ratio,
                products: order1Total,
                freight: order1Freight,
                discount: order1Discount,
                replacementDiscount: order1ReplacementDiscount,
                sellTotal: order1SellTotal,
              },
              {
                label: order2Label,
                accent: 'text-emerald-700',
                ratio: order2Ratio,
                products: order2Total,
                freight: order2Freight,
                discount: order2Discount,
                replacementDiscount: order2ReplacementDiscount,
                sellTotal: order2SellTotal,
              },
            ]).map(child => (
              <div key={child.label} className="bg-white border border-slate-200 rounded-lg p-3">
                <div className="flex items-baseline justify-between gap-2">
                  <span className={`font-medium ${child.accent}`}>{child.label}</span>
                  <span className="text-xs text-slate-500">{(child.ratio * 100).toFixed(1)}% dos produtos</span>
                </div>
                <dl className="mt-2 space-y-1 text-xs text-slate-600">
                  <div className="flex justify-between">
                    <dt>Produtos</dt>
                    <dd className="tabular-nums">{formatCurrency(child.products)}</dd>
                  </div>
                  {shippingPrice > 0 && (
                    <div className="flex justify-between">
                      <dt>Frete (rateado)</dt>
                      <dd className="tabular-nums">{formatCurrency(child.freight)}</dd>
                    </div>
                  )}
                  {orderDiscount > 0 && (
                    <div className="flex justify-between">
                      <dt>Desconto (rateado)</dt>
                      <dd className="tabular-nums text-red-600">− {formatCurrency(child.discount)}</dd>
                    </div>
                  )}
                  {child.replacementDiscount > 0 && (
                    <div className="flex justify-between">
                      <dt>Desc. troca/devolução</dt>
                      <dd className="tabular-nums text-red-600">− {formatCurrency(child.replacementDiscount)}</dd>
                    </div>
                  )}
                </dl>
                <div className="mt-2 pt-2 border-t border-slate-200 flex justify-between items-baseline">
                  <span className="text-xs font-semibold text-slate-700 uppercase">Total do pedido</span>
                  <span className={`text-base font-bold ${child.accent} tabular-nums`}>
                    {formatCurrency(child.sellTotal)}
                  </span>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs text-slate-500">
            Soma dos dois pedidos: <span className="font-semibold tabular-nums">{formatCurrency(round2(order1SellTotal + order2SellTotal))}</span>
            {' '}— frete e desconto do pedido são rateados proporcionalmente ao valor dos produtos de cada pedido filho.
          </p>
        </div>

        {/* Replacement items assignment */}
        {hasReplacementItems && (
          <div className="mt-4 border border-purple-200 rounded-lg p-4 bg-purple-50">
            <h4 className="text-xs font-semibold text-purple-700 uppercase mb-2 flex items-center gap-1">
              <RefreshCw size={14} />
              Itens de Troca ({replacementItems.length})
            </h4>
            <p className="text-xs text-purple-600 mb-3">
              Os itens de troca devem pertencer a um único pedido filho. Selecione o destino:
            </p>
            <div className="flex gap-3">
              <label className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer ${replacementTarget === 1 ? 'border-blue-400 bg-blue-50' : 'border-slate-200 bg-white'}`}>
                <input type="radio" name="replacement_target" checked={replacementTarget === 1} onChange={() => setReplacementTarget(1)} className="text-blue-600" />
                <span className="text-sm font-medium">{order1Label}</span>
              </label>
              <label className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer ${replacementTarget === 2 ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200 bg-white'}`}>
                <input type="radio" name="replacement_target" checked={replacementTarget === 2} onChange={() => setReplacementTarget(2)} className="text-emerald-600" />
                <span className="text-sm font-medium">{order2Label}</span>
              </label>
            </div>
            <div className="mt-2 text-xs text-purple-500">
              {replacementItems.map((ri, idx) => (
                <span key={idx} className="inline-block bg-purple-100 text-purple-700 rounded px-2 py-0.5 mr-1 mt-1">
                  {ri.sku} x{ri.quantity}
                </span>
              ))}
            </div>
            {order.product_replacement_discount_value && order.product_replacement_discount_value > 0 && (
              <p className="text-xs text-purple-600 mt-2">
                Desconto de troca (R$ {order.product_replacement_discount_value.toFixed(2)}) será atribuído ao {replacementTarget === 1 ? order1Label : order2Label}.
              </p>
            )}
          </div>
        )}

        {/* Validation warning */}
        {!canConfirm && (
          <div className="mt-4 flex items-center gap-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
            <AlertTriangle size={16} />
            <span>
              {!order2HasItems
                ? `Mova pelo menos um item para o ${order2Label}.`
                : !order1HasItems
                  ? `O ${order1Label} precisa ter pelo menos um item.`
                  : 'Todos os itens devem estar totalmente distribuídos.'}
            </span>
          </div>
        )}
      </div>

      <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-lg flex justify-end gap-3">
        {error && (
          <div className="flex-1 flex items-center gap-2 text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm">
            <AlertTriangle size={16} className="flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}
        <Button variant="outline" onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={!canConfirm || loading}
          loading={loading}
        >
          Confirmar Desmembramento
        </Button>
      </div>
    </Modal>
  );
};
